import {
    OptionDefinition,
    OptionGroup,
} from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { useCallback, useEffect, useRef, useState } from "react";
import { MapEngineHandle, fetchCatalog, getVersionsForDropdown } from "./index";
import { MapSide, SideConfig, useViewerStore } from "../state";
import { SideOptions, SelectOption } from "../ui/ConfigBar";

const SOURCE_OPTIONS: OptionDefinition[] = [
    { label: "MEDAS Prod", value: "medas_prod" },
    { label: "MEDAS Gamma", value: "medas_gamma" },
];

/**
 * Country-groups the catalog into sorted Select option groups (feedback #5):
 * groups A→Z by country label, stylesheets A→Z within each. Consumes the
 * already-grouped `dropdown` from formatLiveBasemapData rather than re-deriving.
 */
function buildStylesheetGroups(catalogDropdown: any[]): SelectOption[] {
    const groups: OptionGroup[] = (catalogDropdown ?? [])
        .filter((g) => g && Array.isArray(g.options) && g.options.length > 0)
        .map((g) => ({
            label: g.label,
            options: [...g.options].sort((a: OptionDefinition, b: OptionDefinition) =>
                (a.label ?? "").localeCompare(b.label ?? "")
            ),
        }));
    groups.sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
    return groups;
}

const EMPTY_SIDE_OPTIONS: SideOptions = {
    sourceOptions: SOURCE_OPTIONS,
    stylesheetOptions: [],
    versionOptions: [],
};

/** Everything a single-view layout needs to render and drive the map. */
export interface ViewerController {
    draft: { left: SideConfig; right: SideConfig };
    options: SideOptions;
    dirty: boolean;
    refreshing: boolean;
    updating: boolean;
    /** True until the first auto-load (catalog + default style) completes. */
    initializing: boolean;
    onChange: (side: MapSide, patch: Partial<SideConfig>) => void;
    onRefresh: () => void;
    onUpdate: () => void;
    /** Pass to MapContainer's onReady. */
    onMapReady: (handle: MapEngineHandle) => void;
    /** The engine handle once the map is ready (for side panels). */
    handle: MapEngineHandle | null;
}

/**
 * Shared controller for the single-view layouts. Owns store wiring, the
 * Refresh (reload catalog/versions) and Update (apply selection to the map)
 * actions, and — importantly — an auto-load on first render so the default
 * stylesheet renders without the user having to click Refresh then Update.
 *
 * Presentational layout variants consume this and only differ in chrome.
 */
export function useViewerController(): ViewerController {
    const draft = useViewerStore((s) => s.draft);
    const setDraft = useViewerStore((s) => s.setDraft);
    const applySide = useViewerStore((s) => s.applySide);
    const isDirty = useViewerStore((s) => s.isDirty);

    const [options, setOptions] = useState<SideOptions>(EMPTY_SIDE_OPTIONS);
    const [refreshing, setRefreshing] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [handle, setHandle] = useState<MapEngineHandle | null>(null);
    const handleRef = useRef<MapEngineHandle | null>(null);
    const autoLoadedRef = useRef(false);
    const lastCatalogRef = useRef<any>(null);
    const selectedStylesheetId = draft.left.stylesheetId;

    const buildSideOptions = useCallback(
        (catalog: any, stylesheetId: string): SideOptions => {
            // #5: country-grouped, sorted stylesheet options (reuse catalog.dropdown).
            const stylesheetOptions = buildStylesheetGroups(catalog?.dropdown);
            // #7: ACTIVE/EXPERIMENTAL/DEPRECATED-grouped versions (reuse getVersionsForDropdown).
            const versionOptions: SelectOption[] =
                stylesheetId && catalog?.formatted?.[stylesheetId]
                    ? getVersionsForDropdown(stylesheetId, catalog.formatted)
                    : [];
            return { sourceOptions: SOURCE_OPTIONS, stylesheetOptions, versionOptions };
        },
        []
    );

    // Reload the catalog + version lists only (does NOT touch the map).
    const refreshCatalog = useCallback(async () => {
        const left = useViewerStore.getState().draft.left;
        const catalog = await fetchCatalog(left.environment);
        lastCatalogRef.current = catalog;
        setOptions(buildSideOptions(catalog, left.stylesheetId));
        return catalog;
    }, [buildSideOptions]);

    // When the selected stylesheet changes, recompute the (status-grouped)
    // version options from the cached catalog — no refetch needed.
    useEffect(() => {
        if (lastCatalogRef.current) {
            setOptions(buildSideOptions(lastCatalogRef.current, selectedStylesheetId));
        }
    }, [selectedStylesheetId, buildSideOptions]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await refreshCatalog();
        } finally {
            setRefreshing(false);
        }
    }, [refreshCatalog]);

    // Apply the current selection to the map (fetch style + tileset override).
    const onUpdate = useCallback(async () => {
        if (!handleRef.current) return;
        setUpdating(true);
        try {
            // Only mark the draft applied if the style actually loaded — otherwise
            // the dirty indicator would clear on a failed update, misleading the user.
            const applied = await handleRef.current.applyConfig(
                useViewerStore.getState().draft.left
            );
            if (applied) applySide("left");
        } finally {
            setUpdating(false);
        }
    }, [applySide]);

    const onChange = useCallback(
        (side: MapSide, patch: Partial<SideConfig>) => setDraft(side, patch),
        [setDraft]
    );

    // Auto-load on first map-ready: populate dropdowns AND render the default
    // stylesheet, so the viewer shows a map immediately (no manual Refresh+Update).
    const onMapReady = useCallback(
        (handle: MapEngineHandle) => {
            handleRef.current = handle;
            setHandle(handle);
            if (autoLoadedRef.current) return;
            autoLoadedRef.current = true;
            (async () => {
                try {
                    await refreshCatalog();
                    const applied = await handle.applyConfig(
                        useViewerStore.getState().draft.left
                    );
                    if (applied) applySide("left");
                } catch {
                    // Non-fatal: user can still Refresh/Update manually.
                } finally {
                    setInitializing(false);
                }
            })();
        },
        [refreshCatalog, applySide]
    );

    // Safety: clear the initializing flag even if map-ready never fires.
    useEffect(() => {
        const t = setTimeout(() => setInitializing(false), 15000);
        return () => clearTimeout(t);
    }, []);

    return {
        draft,
        options,
        dirty: isDirty("left"),
        refreshing,
        updating,
        initializing,
        onChange,
        onRefresh,
        onUpdate,
        onMapReady,
        handle,
    };
}
